/* Shared view controls: restore pan/spin and initialize without mutation-debounce starvation. */
(async () => {
  if (window.inspireViewerPan === 'pan-spin-v3') return;
  window.inspireViewerPan = 'pan-spin-v3';
  const THREE = await import('https://esm.sh/three@0.183.0');
  const style = document.createElement('style');
  style.textContent = '.viewer-pan{font:inherit;margin:8px 0 12px;padding:0 12px}.viewer-pan summary{cursor:pointer;text-align:center}.viewer-pan label{display:grid;grid-template-columns:75px minmax(0,1fr);align-items:center;gap:10px;margin:10px 0}.viewer-pan output{grid-column:2;text-align:right;font-size:.85em}.viewer-pan input{width:100%;min-width:0;accent-color:#111}.viewer-pan button{display:block;margin:10px auto;background:white;color:inherit;border:1px solid #aaa;padding:6px 12px;font:inherit;cursor:pointer}.viewer-pan :focus-visible{outline:2px solid currentColor;outline-offset:3px}';
  document.head.append(style);
  function controls(parent, resource, move, center, spin) {
    const panel = document.createElement('details');
    panel.className = 'viewer-pan';
    panel.dataset.ihFeature = 'viewer_pan';
    panel.dataset.ihResource = resource;
    panel.innerHTML = '<summary>Move &amp; rotate view</summary><label>Horizontal<input type="range" min="-100" max="100" value="0" step="1" aria-label="Move view horizontally"></label><label>Vertical<input type="range" min="-100" max="100" value="0" step="1" aria-label="Move view vertically"></label><label>Spin<input type="range" min="-180" max="180" value="0" step="1" aria-label="Spin viewing plane"><output aria-live="off">0\u00b0</output></label><button type="button">Reset view</button>';
    const inputs = [...panel.querySelectorAll('input')];
    let last = [0, 0, 0];
    const clear = () => { last = [0, 0, 0]; inputs.forEach(i => { i.value = '0'; }); panel.querySelector('output').textContent = '0\u00b0'; };
    inputs.forEach((input, axis) => {
      input.addEventListener('input', () => {
        const value = Number(input.value);
        if (axis === 2) { spin((value-last[axis])*Math.PI/180); panel.querySelector('output').textContent = value+'\u00b0'; }
        else move(axis, (value - last[axis]) / 100);
        last[axis] = value;
      });
      input.addEventListener('change', () => window.InspireEngagement?.track('viewer_pan', ['horizontal','vertical','spin'][axis], resource));
    });
    panel.querySelector('button').addEventListener('click', () => {
      center(); clear();
      window.InspireEngagement?.track('viewer_pan', 'reset', resource);
    });
    parent.append(panel);
    document.dispatchEvent(new CustomEvent('product-info:loaded'));
    clear.panel = panel;
    return clear;
  }
  function solo(viewer) {
    if (viewer.inspirePanState) {
      const { panel } = viewer.inspirePanState;
      if (panel.parentElement !== viewer.parentElement) viewer.parentElement.append(panel);
      return;
    }
    if (!viewer.loaded) return;
    viewer.dataset.panReady = 'true';
    // Existing text explains rotation; avoid the built-in camera wiggle while panning.
    viewer.interactionPrompt = 'none';
    let origin, extent, initialOrbit, initialFov, initialOrientation, initialQuaternion;
    const quaternion = new THREE.Quaternion();
    const parseOrientation = value => {
      const angles=(value || '0deg 0deg 0deg').trim().split(/\s+/).map(x=>parseFloat(x)*(x.endsWith('rad')?1:Math.PI/180));
      return new THREE.Quaternion().setFromEuler(new THREE.Euler(angles[1],angles[2],angles[0],'YXZ'));
    };
    const setTarget = p => {
      const o = viewer.getCameraOrbit();
      viewer.cameraTarget = `${p.x}m ${p.y}m ${p.z}m`;
      viewer.cameraOrbit = `${o.theta}rad ${o.phi}rad ${o.radius}m`;
      viewer.jumpCameraToGoal();
    };
    const clear = controls(viewer.parentElement, 'product', (axis, delta) => {
      const o = viewer.getCameraOrbit(), p = viewer.getCameraTarget();
      const t = o.theta, f = o.phi;
      const v = axis ? [-Math.sin(t)*Math.cos(f), Math.sin(f), -Math.cos(t)*Math.cos(f)] : [Math.cos(t), 0, -Math.sin(t)];
      // Translate camera and target together so the object moves with the slider.
      setTarget({ x:p.x-v[0]*delta*extent, y:p.y-v[1]*delta*extent, z:p.z-v[2]*delta*extent });
    }, () => {
      quaternion.copy(initialQuaternion);
      viewer.orientation = initialOrientation || '0deg 0deg 0deg';
      viewer.requestUpdate('orientation');
      viewer.cameraTarget = `${origin.x}m ${origin.y}m ${origin.z}m`;
      viewer.cameraOrbit = `${initialOrbit.theta}rad ${initialOrbit.phi}rad ${initialOrbit.radius}m`;
      viewer.fieldOfView = `${initialFov}deg`;
      viewer.jumpCameraToGoal();
    }, delta => {
      const o=viewer.getCameraOrbit();
      const axis=new THREE.Vector3(Math.sin(o.theta)*Math.sin(o.phi),Math.cos(o.phi),Math.cos(o.theta)*Math.sin(o.phi));
      quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(axis,-delta)).normalize();
      const e=new THREE.Euler().setFromQuaternion(quaternion,'YXZ');
      viewer.orientation = `${e.z}rad ${e.x}rad ${e.y}rad`;
      viewer.requestUpdate('orientation');
      viewer.updateComplete.then(async () => {
        const target={...viewer.getCameraTarget()}, orbit={...viewer.getCameraOrbit()}, fov=viewer.getFieldOfView();
        await viewer.updateFraming();
        viewer.cameraTarget=`${target.x}m ${target.y}m ${target.z}m`;
        viewer.cameraOrbit=`${orbit.theta}rad ${orbit.phi}rad ${orbit.radius}m`;
        viewer.fieldOfView=`${fov}deg`;viewer.jumpCameraToGoal();
      });
    });
    viewer.inspirePanState = { panel: clear.panel };
    async function reset() {
      await viewer.updateComplete;
      if(viewer.dataset.initialCameraOrbit)viewer.cameraOrbit=viewer.dataset.initialCameraOrbit;
      if(viewer.dataset.initialFieldOfView)viewer.fieldOfView=viewer.dataset.initialFieldOfView;
      await viewer.updateComplete;
      viewer.jumpCameraToGoal();
      await new Promise(resolve=>requestAnimationFrame(()=>requestAnimationFrame(resolve)));
      origin = {...viewer.getCameraTarget()};
      initialOrbit = {...viewer.getCameraOrbit()};
      initialFov = viewer.getFieldOfView();
      initialOrientation = viewer.getAttribute('orientation') || '';
      initialQuaternion = parseOrientation(initialOrientation);
      quaternion.copy(initialQuaternion);
      const d = viewer.getDimensions();
      extent = Math.max(d.x, d.y, d.z) * .65;
      clear();
    }
    reset();
    viewer.addEventListener('load', reset);
  }
  function hand(element) {
    const scene = element.handScene;
    if (!scene?.hardware || !scene.framingCenter) return;
    const parent = element.querySelector('.variant-model__comparison-body');
    if (!parent) return;
    if (element.inspirePanState?.scene === scene) {
      const { panel } = element.inspirePanState;
      if (panel.parentElement !== parent) parent.append(panel);
      return;
    }
    element.inspirePanState?.panel.remove();
    element.dataset.panReady = 'true';
    const group=new THREE.Group();
    scene.scene.add(group); group.add(scene.hardware,scene.hand);
    let initialPosition,initialTarget;
    const capture=()=>{initialPosition=scene.camera.position.clone();initialTarget=scene.orbit.target.clone();};
    capture();
    const unspin=()=>{group.quaternion.identity();group.position.set(0,0,0);group.updateMatrixWorld(true);};
    const translate = delta => {
      scene.camera.position.add(delta); scene.orbit.target.add(delta);
      scene.orbit.update(); scene.render();
    };
    const clear = controls(element.querySelector('.variant-model__comparison-body'), 'hand', (axis, delta) => {
      scene.camera.updateMatrixWorld();
      const size = scene.framingSize;
      const v = scene.camera.position.clone().setFromMatrixColumn(scene.camera.matrixWorld, axis);
      translate(v.multiplyScalar(-delta * Math.max(size.x, size.y, size.z) * .65));
    }, () => {
      unspin(); scene.camera.position.copy(initialPosition);scene.orbit.target.copy(initialTarget);scene.orbit.update();scene.render();
    }, delta => {
      const axis=scene.camera.position.clone().sub(scene.orbit.target).normalize();
      group.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(axis,-delta)).normalize();
      const center=scene.framingCenter;
      group.position.copy(center).sub(center.clone().applyQuaternion(group.quaternion));
      group.updateMatrixWorld(true);scene.render();
    });
    element.inspirePanState = { panel: clear.panel, scene };
    const fit = scene.fitCameraToScene;
    scene.fitCameraToScene = function (...args) { unspin(); const result = fit.apply(this, args); capture(); clear(); return result; };
  }
  let pending;
  function scan() {
    // Coalesce mutations without indefinitely postponing initialization on active pages.
    if (pending) return;
    pending = setTimeout(() => {
      pending = null;
      document.querySelectorAll('.variant-model__viewer').forEach(solo);
      document.querySelectorAll('[data-hand-comparison]').forEach(hand);
    }, 80);
  }
  document.addEventListener('load', scan, true);
  document.addEventListener('product-info:loaded', scan);
  new MutationObserver(scan).observe(document.body, { childList:true, subtree:true, attributes:true, attributeFilter:['data-scale-validation'] });
  scan();
})().catch(error => console.error('Viewer controls unavailable', error));
